import { useMe } from "@/api";
import { isHidden } from "@/lib/hidden-features";
import { useState, useRef, useEffect } from "react";
import {
  Box,
  Card,
  Group,
  Stack,
  Text,
  ThemeIcon,
  RingProgress,
  Tabs,
  Badge,
  Button,
  Timeline,
  Image,
  useMantineTheme,
  Rating,
  Notification,
  Modal,
  ActionIcon,
  useMantineColorScheme,
  Slider,
  Flex,
  Divider,
  Loader,
} from "@mantine/core";
import {
  CaretRightIcon,
  BookmarkSimpleIcon,
  SpeakerHighIcon,
  XIcon,
  ArrowCounterClockwiseIcon,
  ArrowClockwiseIcon,
  PlayIcon,
  PauseIcon,
  PlusIcon,
  CaretLeftIcon,
} from "@phosphor-icons/react";
import {
  // usePathSpeech,
  usePaths,
  useBookmarkedSessions,
  useUpdateSession,
  type PathSession,
} from "@/api";


export default function InProgress() {
  const { data: me } = useMe();
  const { colorScheme } = useMantineColorScheme();
  const { data: inProgressDataList, refetch: refetchInProgressList } =
    usePaths({
      status: "in_progress",
    });
  const { mutateAsync: updatePathSession } = useUpdateSession();

  const [openPath, setOpenPath] = useState<string | null>(null);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string | null>("active");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<PathSession | null>(
    null
  );
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState(""); // new state for message
  const [isLoading, setIsLoading] = useState(false);

  const [bookmarkModalOpen, setBookmarkModalOpen] = useState(false);
  const [selectedBookmarkSession, setSelectedBookmarkSession] =
    useState<any>(null);
  const [showAudioPlayer, setShowAudioPlayer] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  // const [audioTrigger, setAudioTrigger] = useState<number | null>(null);

  const { data: bookmarkList = [], refetch: refetchBookmarkList } =
    useBookmarkedSessions(Number(openPath) ?? 0, {
      query: {
        queryKey: ["bookmark"],
      },
    });

  // const { data: audiolist } =
  //   usePathSpeech(audioTrigger ?? 0, {
  //     query: {
  //       queryKey: ["audio", audioTrigger],
  //       enabled: audioTrigger !== null,
  //     },
  //   });

  const theme = useMantineTheme();

  // Audio functionality
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.addEventListener("loadedmetadata", () => {
        setDuration(audioRef.current?.duration || 0);
      });

      audioRef.current.addEventListener("timeupdate", () => {
        setCurrentTime(audioRef.current?.currentTime || 0);
      });

      audioRef.current.addEventListener("ended", () => {
        setIsPlaying(false);
      });
    }
  }, []);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const skipBackward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(
        0,
        audioRef.current.currentTime - 10
      );
    }
  };

  const skipForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(
        duration,
        audioRef.current.currentTime + 10
      );
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  console.log("inProgressDataList?.results?.", inProgressDataList?.results)
  return (
    <>
      <Box>
        {/* Success Notification */}
        {showSuccessNotification && (
          <Notification
            color="teal"
            mt="md"
            onClose={() => setShowSuccessNotification(false)}
          >
            {successMessage}
          </Notification>
        )}

        {inProgressDataList?.results?.map((item: any, index: number) => {
          const key = item.id ?? index;
          const isOpen = openPath === String(key);
          return (
            <Card
              withBorder
              radius="md"
              mb="md"
              p="16px"
              key={key}
              onMouseEnter={() => setHoveredCard(String(key))}
              onMouseLeave={() => setHoveredCard(null)}
              style={{
                background:
                  isOpen || hoveredCard === String(key)
                    ? colorScheme === "dark"
                      ? theme.colors.neutral[9]
                      : "linear-gradient(90deg, #FFF8F6 0%, #FAF8FB 50%, #F5F8FA 100%)"
                    : colorScheme === "dark"
                      ? theme.colors.neutral[9]
                      : "#F1F7FE",
                transition: "background 0.2s ease",
              }}
            >
              <svg width="0" height="0">
                <defs>
                  <linearGradient id="ringGradient" x1="1" y1="0" x2="0" y2="1">
                    <stop
                      offset="0%"
                      stopColor={
                        colorScheme === "dark"
                          ? theme.colors.negative[3]
                          : theme.colors.negative[3]
                      }
                    />
                    <stop
                      offset="100%"
                      stopColor={
                        colorScheme === "dark"
                          ? theme.colors.indigo[8]
                          : theme.colors.indigo[8]
                      }
                    />
                  </linearGradient>
                </defs>
              </svg>
              <Group
                justify="space-between"
                align="flex-start"
                onClick={() =>
                  setOpenPath(isOpen ? null : String(item.id ?? index))
                }
                style={{ cursor: "pointer" }}
              >
                <Stack gap="md" w="80%">
                  <Group justify="space-between" align="start">
                    <Stack gap="xs" align="flex-start">
                      <Group gap="xs" align="center">
                        <Image
                          src={"/assets/in_progress.png"}
                          h={24}
                          w={24}
                          fit="contain"
                        />
                        <Text
                          c={
                            item?.status === "new" || item?.status === "Not started"
                              ? colorScheme === "dark" ? "#FA7D87" : theme.colors.red[8]
                              : item?.status === "in_progress"
                                ? colorScheme === "dark" ? "#FA7D87" : theme.colors.red[8]
                                : colorScheme === "dark" ? "#4B9BE5" : theme.colors.red[8]
                          }
                          variant="subtle"
                          fw={500}
                          style={{
                            width: "89.83px",
                            height: "24.8px",
                            display: "flex",
                            alignItems: "center",
                            fontSize: "16px",
                            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif"
                          }}
                        >
                          {item?.status === "completed" ? "Completed" :
                            item?.status === "in_progress" ? "In Progress" :
                              item?.status === "new" ? "Not started" :
                                item?.status || "In Progress"}
                        </Text>
                      </Group>

                      <Text
                        fw={500}
                        style={{
                          fontSize: "18px",
                          color: colorScheme === "dark" ? "white" : "#393939",
                          lineHeight: "120%",
                        }}
                        mt="xs"
                      >
                        {item?.name ?? ""}
                      </Text>

                      {item?.description && (
                        <Text
                          size="sm"
                          c={colorScheme === "dark" ? theme.colors.neutral[3] : theme.colors.neutral[7]}
                          style={{
                            lineHeight: 1.5,
                            marginTop: "4px"
                          }}
                        >
                          {item?.description}
                        </Text>
                      )}
                    </Stack>
                  </Group>
                </Stack>

                <RingProgress
                  size={120}
                  thickness={4}
                  roundCaps
                  sections={[
                    {
                      value: item?.progress ?? 0,

                      color: "url(#ringGradient)",
                    },
                  ]}
                  label={
                    <Box ta="center">
                      <Text
                        fw={600}
                        size="md"
                        c={
                          colorScheme === "dark"
                            ? theme.colors.indigo[4]
                            : theme.colors.indigo[9]
                        }
                      >
                        {item?.progress ?? 0}%
                      </Text>
                      <Text size="sm" c="dimmed">
                        Progress
                      </Text>
                    </Box>
                  }
                />
              </Group>
              {isOpen && (
                <Card
                  mt="lg"
                  bg={
                    colorScheme === "dark"
                      ? theme.colors.neutral[9]
                      : "white"
                  }
                  withBorder
                  radius="lg"
                  p={"xs"}
                >
                  {/* <ScrollArea h={300} type="auto" > */}
                  <Tabs
                    value={activeTab}
                    onChange={setActiveTab}
                    styles={{ tab: { fontSize: 16, padding: "12px 20px" } }}
                  >
                    <Tabs.List grow>
                      <Tabs.Tab
                        value="active"
                        style={{
                          background:
                            activeTab === "active"
                              ? "linear-gradient(90deg, #0b36b0 0%, #6b3a91 40%, #ff7373 100%)"
                              : undefined,
                          color: activeTab === "active" ? "white" : undefined,
                          borderTopLeftRadius: "9px",
                          borderTopRightRadius: "9px",
                        }}
                        onMouseEnter={(e) => {
                          if (activeTab !== "active") {
                            e.currentTarget.style.backgroundColor = colorScheme === "dark"
                              ? "#555555"
                              : "#E0EDFE";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (activeTab !== "active") {
                            e.currentTarget.style.backgroundColor = "transparent";
                          }
                        }}
                      >
                        Active Path
                      </Tabs.Tab>
                      <Tabs.Tab
                        value="bookmark"
                        style={{
                          background:
                            activeTab === "bookmark"
                              ? "linear-gradient(90deg, #0b36b0 0%, #6b3a91 40%, #ff7373 100%)"
                              : undefined,
                          color: activeTab === "bookmark" ? "white" : undefined,
                          borderTopLeftRadius: "9px",
                          borderTopRightRadius: "9px",
                        }}
                        onMouseEnter={(e) => {
                          if (activeTab !== "bookmark") {
                            e.currentTarget.style.backgroundColor = colorScheme === "dark"
                              ? "#555555"
                              : "#E0EDFE";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (activeTab !== "bookmark") {
                            e.currentTarget.style.backgroundColor = "transparent";
                          }
                        }}
                      >
                        Bookmark
                      </Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value="active" pt="xs">
                      <Stack mt="md">
                        <Group>
                          <Text fw={600} c={"gray.6"}>
                            Path Progress
                          </Text>
                          {!isHidden("listen") && (
                            <Button
                              leftSection={<SpeakerHighIcon size={16} />}
                              ml="auto"
                              size="xs"
                              variant="filled"
                              onClick={() => setShowAudioPlayer(!showAudioPlayer)}
                            >
                              Listen
                            </Button>
                          )}
                        </Group>

                        {showAudioPlayer && (
                          <Card withBorder radius="md" p="md" mt="md">
                            <Group justify="space-between" mb="md">
                              <Text fw={600} size="lg">
                                Listen your Path
                              </Text>
                              <ActionIcon
                                variant="subtle"
                                onClick={() => {
                                  setShowAudioPlayer(false);
                                  // setAudioTrigger(null);
                                }}
                              >
                                <XIcon size={20} />
                              </ActionIcon>
                            </Group>

                            <Box
                              p="xs"
                              style={{
                                background: `linear-gradient(90deg, ${theme.colors.indigo[8]} 0%, ${theme.colors.negative[4]} 100%)`,
                                borderRadius: theme.radius.md,
                                color: "white",
                              }}
                            >
                              <Group align="center" gap="xs">
                                <ActionIcon
                                  size="lg"
                                  variant="subtle"
                                  color="white"
                                  onClick={skipBackward}
                                >
                                  <ArrowCounterClockwiseIcon size={24} />
                                </ActionIcon>

                                <ActionIcon
                                  radius="xl"
                                  size="xl"
                                  variant="filled"
                                  color="white"
                                  style={{
                                    backgroundColor: "white",
                                    color: theme.colors.indigo[8],
                                  }}
                                  onClick={togglePlay}
                                >
                                  {isPlaying ? (
                                    <PauseIcon size={24} weight="fill" />
                                  ) : (
                                    <PlayIcon size={24} weight="fill" />
                                  )}
                                </ActionIcon>

                                <ActionIcon
                                  size="lg"
                                  variant="subtle"
                                  color="white"
                                  onClick={skipForward}
                                >
                                  <ArrowClockwiseIcon size={24} />
                                </ActionIcon>
                                <Text
                                  size="sm"
                                  c={theme.colors.neutral[2]}
                                  style={{ minWidth: 45 }}
                                >
                                  {formatTime(duration)}
                                </Text>

                                <Slider
                                  value={currentTime}
                                  onChange={(value) => {
                                    if (audioRef.current) {
                                      audioRef.current.currentTime = value;
                                    }
                                  }}
                                  max={duration}
                                  min={0}
                                  step={0.1}
                                  style={{ flex: 1 }}
                                  size="sm"
                                  color="white"
                                  styles={{
                                    track: {
                                      backgroundColor: theme.colors.blue[5],
                                      border: "none",
                                    },
                                    bar: {
                                      background: theme.colors.blue[5],
                                    },
                                    thumb: {
                                      boxSizing: "content-box",
                                    },
                                  }}
                                />
                              </Group>
                            </Box>

                            {/* Track Your Blood Pressure Section */}
                          </Card>
                        )}
                        <Timeline
                          active={(() => {
                            const sessions = item?.path_sessions || [];

                            let lastActiveIndex = -1;
                            for (let i = 0; i < sessions.length; i++) {
                              if (sessions[i]?.status === "completed" || sessions[i]?.status === "in_progress") {
                                lastActiveIndex = i;
                              }
                            }
                            return lastActiveIndex + 1;
                          })()}
                          bulletSize={24}
                          lineWidth={2}
                          ml={"xs"}
                        >
                          {item?.path_sessions?.map((list: any, i: number) => {
                            const isDisabled = i > 0 && item?.path_sessions?.[i - 1]?.status === "in_progress";
                            return (
                              <Timeline.Item
                                style={{
                                  cursor: isDisabled ? "not-allowed" : "pointer",
                                  opacity: isDisabled ? 0.5 : 1
                                }}
                                key={i}
                                bullet={
                                  <ThemeIcon
                                    variant="filled"
                                    color={
                                      isDisabled
                                        ? "gray"
                                        : list?.status === "in_progress"
                                          ? undefined
                                          : list?.status === "completed"
                                            ? "green"
                                            : list?.status === "new"
                                              ? "blue"
                                              : "gray"
                                    }
                                    style={{
                                      backgroundColor: list?.status === "in_progress" && !isDisabled ? "#1662B3" : undefined
                                    }}
                                    radius="xl"
                                    size="md"
                                  >
                                    <Text size="sm">{i + 1}</Text>
                                  </ThemeIcon>
                                }
                                onClick={() => {
                                  if (!isDisabled) {
                                    setSelectedSession(list);
                                    setModalOpen(true);
                                  }
                                }}
                              >
                                <Group
                                  justify="space-between"
                                  align="center"
                                  mb={4}
                                >
                                  <Group>
                                    <Text fw={600} c={isDisabled ? "dimmed" : undefined}>
                                      {list?.title}
                                    </Text>
                                    <Badge
                                      variant="filled"
                                      style={{
                                        backgroundColor: isDisabled
                                          ? (colorScheme === "dark" ? "#727272" : "#E3EDFB")
                                          : (colorScheme === "dark" ? "#727272" : "#E3EDFB"),
                                        color: isDisabled
                                          ? (colorScheme === "dark" ? "#FAFAFA" : "#124B8C")
                                          : (colorScheme === "dark" ? "#FAFAFA" : "#124B8C")
                                      }}
                                    >
                                      {list?.status === "completed" ? "COMPLETED" :
                                        list?.status === "in_progress" ? "IN_PROGRESS" :
                                          list?.status === "new" ? "New" :
                                            list?.status ?? ""}
                                    </Badge>
                                  </Group>

                                  {list?.status === "current" && !isDisabled && (
                                    <Group gap="xs">
                                      <Button variant="outline" size="xs">
                                        Complete Now
                                      </Button>
                                      <CaretRightIcon size={16} />
                                    </Group>
                                  )}
                                </Group>
                                <Text size="sm" c={isDisabled ? "dimmed" : "dimmed"}>
                                  {list?.description ?? ""}
                                </Text>
                              </Timeline.Item>
                            );
                          })}
                        </Timeline>
                      </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="bookmark" pt="xs">
                      <Stack mt="md">
                        <Text fw={500} size="ms">
                          Bookmark
                        </Text>

                        {(bookmarkList as any as PathSession[])?.map(
                          (data: any) => (
                            <Group
                              key={data?.id}
                              p="sm"
                              bg={
                                colorScheme === "dark"
                                  ? theme.colors.neutral[8]
                                  : theme.colors.neutral[1]
                              }
                              justify="flex-start"
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                setSelectedBookmarkSession(data);
                                setBookmarkModalOpen(true);
                              }}
                            >
                              <Group>
                                <ThemeIcon
                                  color={theme.colors.informative[7]}
                                  size={30}
                                  radius="xl"
                                >
                                  <BookmarkSimpleIcon
                                    size={20}
                                    color="white"
                                  />
                                </ThemeIcon>

                                <Text size="sm">{data?.title ?? ""}</Text>
                              </Group>

                            </Group>
                          )
                        )}
                      </Stack>
                    </Tabs.Panel>
                  </Tabs>
                </Card>
              )}
            </Card>
          );
        })}
      </Box>
      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        withCloseButton={false}
        centered
        size="xl"
        styles={{
          body: {
            overflowX: "hidden",
            maxWidth: "100%",
            wordWrap: "break-word",
          },
          content: {
            overflowX: "hidden",
            maxWidth: "100%",
            wordWrap: "break-word",
          },
          header: {
            overflowX: "hidden",
            maxWidth: "100%",
            wordWrap: "break-word",
          },
        }}
      >
        {selectedSession && (
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              height: 500,
              overflowX: "hidden",
              maxWidth: "100%",
              wordWrap: "break-word",
              position: "relative",
            }}
          >
            {showSuccessNotification && (
              <Notification
                color="teal"
                mt="md"
                onClose={() => setShowSuccessNotification(false)}
              >
                {successMessage}
              </Notification>
            )}

            {isLoading && (
              <Box
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1000,
                }}
              >
                <Stack align="center" gap="md">
                  <Loader color="blue" size="lg" />
                  <Text size="sm" c="dimmed">
                    Processing...
                  </Text>
                </Stack>
              </Box>
            )}

            <Group justify="space-between" align="center" mb="md">
              <Text
                fw={600}
                size="md"
                style={{
                  color: theme.colors.primary[8],
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                  maxWidth: "100%",
                }}
              >
                {selectedSession?.title || "Session Details"}
              </Text>
              <Group gap="xs">
                {/* <Rating
                  value={selectedSession?.rating ?? 0}
                  fractions={2}
                  size="md"
                  onChange={async (newRating) => {
                    try {
                      await updatePathSession({
                        id: selectedSession.id,
                        data: { rating: newRating },
                      });
                      setSuccessMessage("Rating updated successfully");
                      setShowSuccessNotification(true);
                      setTimeout(() => setShowSuccessNotification(false), 2000);
                      refetchBookmarkList(); // <--
                    } catch (err: any) {
                      console.error("update path session Error: ", err);
                    }

                    setSelectedSession({
                      ...selectedSession,
                      rating: newRating,
                    });
                  }}
                />
                <BookmarkSimpleIcon
                  size={22}
                  color={theme.colors.neutral[8]}
                  weight={selectedSession?.is_bookmarked ? "fill" : "regular"}
                  style={{ cursor: "pointer" }}
                  onClick={async (_value: any) => {
                    try {
                      const newBookmarkValue = !selectedSession.is_bookmarked;
                      await updatePathSession({
                        id: selectedSession.id,
                        data: { is_bookmarked: newBookmarkValue },
                      });
                      setSuccessMessage("Bookmark updated successfully");
                      setShowSuccessNotification(true);
                      setSelectedSession({
                        ...selectedSession,
                        is_bookmarked: newBookmarkValue,
                      });
                      setTimeout(() => setShowSuccessNotification(false), 2000);
                      refetchBookmarkList(); // <--
                    } catch (err: any) {
                      console.error("update path session Error: ", err);
                    }
                  }}
                /> */}
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  onClick={() => setModalOpen(false)}
                  aria-label="Close"
                >
                  <XIcon size={22} />
                </ActionIcon>
              </Group>
            </Group>

            <Box
              style={{
                flex: 1,
                overflowY: "auto",
                maxWidth: "100%",
                wordWrap: "break-word",
                scrollbarWidth: "none", // Firefox
                msOverflowStyle: "none", // IE and Edge
              }}
            >
              <Flex direction={"column"} gap="xs" mb="md">
                {selectedSession.recommendations?.map((recommendation) => (
                  <Flex
                    key={recommendation?.prompt_id}
                    id={recommendation?.prompt_id}
                    direction={"column"}
                  >
                    <Flex w={"100%"} justify={"end"}>

                      <Rating
                        defaultValue={recommendation?.rating}
                        fractions={2}
                        size="md"
                        onChange={async (newRating) => {
                          updatePathSession({
                            id: selectedSession.id,
                            data: {
                              recommendations:
                                selectedSession?.recommendations?.map((rec) => {
                                  if (
                                    rec.prompt_id === recommendation.prompt_id
                                  ) {
                                    return { ...rec, rating: newRating };
                                  }
                                  return rec;
                                }),
                            },
                          }).then(() => {
                            setSuccessMessage("Rating updated successfully");
                            setShowSuccessNotification(true);
                            setTimeout(
                              () => setShowSuccessNotification(false),
                              2000
                            );
                            refetchBookmarkList();
                            refetchInProgressList(); // Add this to refresh active path data
                          });
                        }}
                      />
                      <BookmarkSimpleIcon
                        size={22}
                        color={theme.colors.neutral[8]}
                        key={`${recommendation?.prompt_id}-${recommendation?.is_bookmarked}`}
                        weight={
                          recommendation?.is_bookmarked ? "fill" : "regular"
                        }
                        style={{ cursor: "pointer" }}
                        onClick={async (_value: any) => {
                          try {
                            const newBookmarkValue =
                              !recommendation?.is_bookmarked;
                            updatePathSession({
                              id: selectedSession.id,
                              data: {
                                recommendations:
                                  selectedSession?.recommendations?.map((rec) => {
                                    if (
                                      rec.prompt_id === recommendation.prompt_id
                                    ) {
                                      return { ...rec, is_bookmarked: newBookmarkValue };
                                    }
                                    return rec;
                                  }),
                              },
                            }).then(() => {
                              setSuccessMessage("Bookmark updated successfully");
                              setShowSuccessNotification(true);
                              setSelectedSession({
                                ...selectedSession,
                                is_bookmarked: newBookmarkValue,
                                recommendations:
                                  selectedSession?.recommendations?.map((rec) => {
                                    if (
                                      rec.prompt_id === recommendation.prompt_id
                                    ) {
                                      return {
                                        ...rec,
                                        is_bookmarked: newBookmarkValue,
                                      };
                                    }
                                    return rec;
                                  }),
                              });

                              setTimeout(
                                () => setShowSuccessNotification(false),
                                2000
                              );
                              refetchBookmarkList();
                              refetchInProgressList(); // Add this to refresh active path data
                            });
                          } catch (err: any) {
                            console.error("update path session Error: ", err);
                          }
                        }}
                      />
                    </Flex>
                    <Text
                      size="sm"
                      style={{
                        lineHeight: 1.6,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        whiteSpace: "pre-wrap",
                        maxWidth: "100%",
                      }}
                    >
                      {(recommendation?.prompt_text as string)?.replaceAll(
                        "<user_preferred_name>",
                        me?.first_name ?? "User"
                      ).replaceAll
                        (
                          "||", ""
                        ).replaceAll("{UserPreferredName}", me?.first_name ?? "User")}
                    </Text>
                    <Divider orientation="horizontal" />
                  </Flex>
                ))}
              </Flex>
            </Box>

            <Group justify="center" mt="md" gap="md">
              {/* Comment out the Start button */}
              {/* {selectedSession?.status?.toLowerCase() !== "completed" && (
                <Button
                  variant="filled"
                  color="blue"
                  size="md"
                  radius="md"
                  disabled={
                    selectedSession?.status?.toLowerCase() === "in_progress" || isLoading
                  }
                  loading={isLoading}
                  style={{
                    backgroundColor:
                      selectedSession?.status?.toLowerCase() === "in_progress" || isLoading
                        ? "#ccc"
                        : "#1976D2",
                    color: "white",
                    fontWeight: 600,
                    border: "none",
                  }}
                  onClick={async () => {
                    setIsLoading(true);
                    try {
                      await updatePathSession({
                        id: selectedSession.id,
                        data: { status: "in_progress" },
                      });
                      setSuccessMessage("Session started successfully");
                      setShowSuccessNotification(true);
                      setTimeout(() => setShowSuccessNotification(false), 2000);
                      refetchInProgressList();
                      refetchBookmarkList();
                      setModalOpen(false);
                    } catch (err: any) {
                      console.error("Error starting session: ", err);
                      setSuccessMessage("Error starting session");
                      setShowSuccessNotification(true);
                      setTimeout(() => setShowSuccessNotification(false), 2000);
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                >
                  Start
                </Button>
              )} */}

              <Button
                variant="filled"
                color="blue"
                size="md"
                radius="md"
                disabled={
                  selectedSession?.status?.toLowerCase() === "completed" || isLoading
                }
                loading={isLoading}
                style={{
                  backgroundColor:
                    selectedSession?.status?.toLowerCase() === "completed" || isLoading
                      ? "#ccc"
                      : "#4CAF50",
                  color: "white",
                  fontWeight: 600,
                  border: "none",
                }}
                onClick={async () => {
                  setIsLoading(true);
                  try {
                    await updatePathSession({
                      id: selectedSession.id,
                      data: { status: "completed" },
                    });
                    setSuccessMessage("Session completed successfully");
                    setShowSuccessNotification(true);
                    setTimeout(() => setShowSuccessNotification(false), 2000);
                    refetchInProgressList();
                    refetchBookmarkList();
                    setModalOpen(false);
                  } catch (err: any) {
                    console.error("Error completing session: ", err);
                    setSuccessMessage("Error completing session");
                    setShowSuccessNotification(true);
                    setTimeout(() => setShowSuccessNotification(false), 2000);
                  } finally {
                    setIsLoading(false);
                  }
                }}
              >
                Complete
              </Button>
            </Group>
          </Box>
        )}
      </Modal>
      <Modal
        opened={bookmarkModalOpen}
        onClose={() => setBookmarkModalOpen(false)}
        withCloseButton={false}
        centered
        size="xl"
        styles={{
          body: {
            overflowX: "hidden",
            maxWidth: "100%",
            wordWrap: "break-word",
          },
          content: {
            overflowX: "hidden",
            maxWidth: "100%",
            wordWrap: "break-word",
          },
          header: {
            overflowX: "hidden",
            maxWidth: "100%",
            wordWrap: "break-word",
          },
        }}
      >
        {selectedBookmarkSession && (
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              height: 500,
              overflowX: "hidden",
              maxWidth: "100%",
              wordWrap: "break-word",
            }}
          >
            {showSuccessNotification && (
              <Notification
                color="teal"
                mt="md"
                onClose={() => setShowSuccessNotification(false)}
              >
                {successMessage}
              </Notification>
            )}

            {/* Header */}
            <Group justify="space-between" align="center" mb="md">
              <Text
                fw={600}
                size="md"
                style={{
                  color: theme.colors.primary[8],
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                  maxWidth: "100%",
                }}
              >
                {selectedBookmarkSession?.title || "Bookmarked Session"}
              </Text>
              <Group gap="xs">
                {selectedBookmarkSession.recommendations?.[0] && (
                  <>
                    <Rating
                      defaultValue={selectedBookmarkSession.recommendations[0]?.rating}
                      fractions={2}
                      size="sm"
                      readOnly
                    />
                    <BookmarkSimpleIcon
                      size={20}
                      color={theme.colors.neutral[8]}
                      weight={
                        selectedBookmarkSession.recommendations[0]?.is_bookmarked ? "fill" : "regular"
                      }
                    />
                  </>
                )}
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  onClick={() => setBookmarkModalOpen(false)}
                  aria-label="Close"
                >
                  <XIcon size={22} />
                </ActionIcon>
              </Group>
            </Group>

            {/* Add Note Button */}
            <Group justify="flex-end" mb="md">
              <Button
                leftSection={<PlusIcon size={16} />}
                variant="light"
                color="blue"
                size="sm"
                onClick={() => {
                  // Handle add note functionality
                }}
              >
                Add Note
              </Button>
            </Group>

            {/* Content wrapped in bordered box */}
            <Box
              style={{
                flex: 1,
                overflowY: "auto",
                overflowX: "hidden",
                maxWidth: "100%",
                wordWrap: "break-word",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              <Card withBorder p="md" mb="md">
                <Flex direction={"column"} gap="xs">
                  {selectedBookmarkSession.recommendations?.map((recommendation: any, index: number) => (
                    <Flex
                      key={recommendation?.prompt_id || index}
                      direction={"column"}
                    >
                      <Flex w={"100%"} justify={"end"} mb="xs">
                        <Rating
                          defaultValue={recommendation?.rating}
                          fractions={2}
                          size="md"
                          onChange={async (newRating) => {
                            updatePathSession({
                              id: selectedBookmarkSession.id,
                              data: {
                                recommendations:
                                  selectedBookmarkSession?.recommendations?.map((rec: any) => {
                                    if (
                                      rec.prompt_id === recommendation.prompt_id
                                    ) {
                                      return { ...rec, rating: newRating };
                                    }
                                    return rec;
                                  }),
                              },
                            }).then(() => {
                              setSuccessMessage("Rating updated successfully");
                              setShowSuccessNotification(true);
                              setTimeout(
                                () => setShowSuccessNotification(false),
                                2000
                              );
                              refetchBookmarkList();
                              refetchInProgressList();
                            });
                          }}
                        />
                        <BookmarkSimpleIcon
                          size={22}
                          color={theme.colors.neutral[8]}
                          key={`${recommendation?.prompt_id}-${recommendation?.is_bookmarked}`}
                          weight={
                            recommendation?.is_bookmarked ? "fill" : "regular"
                          }
                          style={{ cursor: "pointer", marginLeft: "8px" }}
                          onClick={async (_value: any) => {
                            try {
                              const newBookmarkValue =
                                !recommendation?.is_bookmarked;
                              updatePathSession({
                                id: selectedBookmarkSession.id,
                                data: {
                                  recommendations:
                                    selectedBookmarkSession?.recommendations?.map((rec: any) => {
                                      if (
                                        rec.prompt_id === recommendation.prompt_id
                                      ) {
                                        return { ...rec, is_bookmarked: newBookmarkValue };
                                      }
                                      return rec;
                                    }),
                                },
                              }).then(() => {
                                setSuccessMessage("Bookmark updated successfully");
                                setShowSuccessNotification(true);
                                setSelectedBookmarkSession({
                                  ...selectedBookmarkSession,
                                  is_bookmarked: newBookmarkValue,
                                  recommendations:
                                    selectedBookmarkSession?.recommendations?.map((rec: any) => {
                                      if (
                                        rec.prompt_id === recommendation.prompt_id
                                      ) {
                                        return {
                                          ...rec,
                                          is_bookmarked: newBookmarkValue,
                                        };
                                      }
                                      return rec;
                                    }),
                                });

                                setTimeout(
                                  () => setShowSuccessNotification(false),
                                  2000
                                );
                                refetchBookmarkList();
                                refetchInProgressList();
                              });
                            } catch (err: any) {
                              console.error("update path session Error: ", err);
                            }
                          }}
                        />
                      </Flex>
                      <Text
                        size="sm"
                        style={{
                          lineHeight: 1.6,
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "pre-wrap",
                          maxWidth: "100%",
                        }}
                      >
                        {(recommendation?.prompt_text as string)?.replaceAll(
                          "<user_preferred_name>",
                          me?.first_name ?? "User"
                        ).replaceAll("||", "")}
                      </Text>
                      {index < (selectedBookmarkSession.recommendations?.length || 0) - 1 && (
                        <Divider orientation="horizontal" mt="md" />
                      )}
                    </Flex>
                  ))}
                </Flex>
              </Card>
            </Box>

            {/* Navigation Footer */}
            <Group justify="space-between" align="center" mt="auto" pt="md">
              <Group gap="xs" style={{ cursor: "pointer" }}>
                <CaretLeftIcon size={16} />
                <Text size="sm" c="dimmed">Previous</Text>
              </Group>
              <Badge variant="light" color="blue" size="lg">
                {selectedBookmarkSession.recommendations?.length ? `1/${selectedBookmarkSession.recommendations.length}` : "1/1"}
              </Badge>
              <Group gap="xs" style={{ cursor: "pointer" }}>
                <Text size="sm" c="blue">Next</Text>
                <CaretRightIcon size={16} />
              </Group>
            </Group>
          </Box>
        )}
      </Modal>

      <audio
        ref={audioRef}
        preload="metadata"
      />
    </>
  );
}
